/* v8 ignore start */
import {createAsyncThunk} from "@reduxjs/toolkit";

import {client} from "@/app/api/client";
import {AppConfiguration} from "@/appConfiguration";
import {TFeedbackType} from "@/components/memories/feedback/feedbackForm";
import {Tracking} from "@/lib/analytics/mixpanel";
import {AnimatedProjectModel} from "@/models/animatedProjectModel";
import {AnimatedSegmentModel} from "@/models/animatedSegmentModel";
import {AutoCreateConfigurationModel} from "@/models/autoCreateConfigurationModel";
import {IResponse, TRootState} from "@/store";
import {
  IAnimateProjectDetails as IAnimatedProjectDetails,
  selectAnimatedProjectIsTextEnabled,
  selectAnimatedProjectMusicTrack,
} from "@/store/slices/animatedProjectSlice/animatedProjectSlice";

export const fetchAnimatedProject = createAsyncThunk(
  "animatedProject/fetchAnimatedProject",
  async (
    {projectId, viewKey}: {projectId: number; viewKey: Nullable<string>},
    {getState}
  ): Promise<AnimatedProjectModel.IJson> => {
    const token = (getState() as TRootState).auth.token;
    let url = `${AppConfiguration.apiBaseUrl}/api/v2/my/animated_projects/${projectId}`;

    if (viewKey) {
      url += `?${new URLSearchParams({vk: viewKey})}`;
    }

    const response = await client.get<IResponse<AnimatedProjectModel.IJson>>(url, {token});

    return response.data.data;
  }
);

export const fetchAnimatedProjectDetails = createAsyncThunk(
  "animatedProject/fetchAnimatedProjectDetails",
  async ({projectId}: {projectId: number}, {getState}): Promise<IAnimatedProjectDetails> => {
    const token = (getState() as TRootState).auth.token;
    const url = `${AppConfiguration.apiBaseUrl}/api/v2/my/animated_projects/${projectId}/details`;

    const response = await client.get<IResponse<IAnimatedProjectDetails>>(url, {token});

    return response.data.data;
  }
);

interface IReCreateProjectOptions {
  musicTrackId: number;
  isTextEnabled: boolean;
  distributionStrategy: AutoCreateConfigurationModel.DistributionStrategy;
}

export const reCreateProject = createAsyncThunk(
  "animatedProject/reCreateProject",
  async (options: Partial<IReCreateProjectOptions>, {getState}): Promise<AnimatedProjectModel.IJson> => {
    Tracking.track("MemoryExplorer_RecreateProjectStarted");
    const state = getState() as TRootState;
    const token = state.auth.token;
    const id = state.animatedProject.project?.id;

    if (!id) throw new Error("Missing project id");

    const data = {
      is_text_enabled: options.isTextEnabled,
      music_track_id: options.musicTrackId,
      distribution_strategy: options.distributionStrategy,
    };

    const url = `${AppConfiguration.apiBaseUrl}/api/v2/my/animated_projects/${id}/recreate`;

    const response = await client.put<IResponse<AnimatedProjectModel.IJson>>(url, data, {token});

    return response.data.data;
  }
);

export const saveFeedback = createAsyncThunk(
  "animatedProject/saveFeedback",
  async ({feedback, feedbackType}: {feedback: string; feedbackType: TFeedbackType}, {getState}) => {
    const state = getState() as TRootState;
    const token = state.auth.token;
    const id = state.animatedProject.project?.id;
    const isTextEnabled = selectAnimatedProjectIsTextEnabled(state);
    const musicTrackId = selectAnimatedProjectMusicTrack(state).id;

    if (!id) throw new Error("Missing project id");

    const url = `${AppConfiguration.apiBaseUrl}/api/v2/my/animated_projects/${id}/save_feedback`;
    client.post(
      url,
      {body: feedback, feedback_type: feedbackType, is_text_enabled: isTextEnabled, music_track_id: musicTrackId},
      {token}
    );
  }
);

export const updateAnimatedProjectName = createAsyncThunk(
  "animatedProject/updateAnimatedProjectName",
  async (name: string, {getState, dispatch}) => {
    const state = getState() as TRootState;
    const token = state.auth.token;
    const id = state.animatedProject.project?.id;

    if (!id) throw new Error("Missing project id");

    const segment = state.animatedProject.project?.segments[0];

    if (!segment) throw new Error("Missing segment");

    let segmentModel = new AnimatedSegmentModel(segment);

    segmentModel = segmentModel.replaceTextWithAutoFit(name);

    const url = `${AppConfiguration.apiBaseUrl}/api/v2/my/animated_projects/${id}/update_name`;
    client.put(url, {name, lottie_animation: segmentModel.lottieAnimation.toObject()}, {token});

    return {name, segment: segmentModel.toObject()};
  }
);

/* v8 ignore stop */
